import { AddItemButton, DeleteItemButton } from '@core/components/Buttons';
import FormTitle from '@core/components/FormTitle';
import { DateInput } from '@core/components/Inputs';
import CodigoBarrasInput from '@core/components/Inputs/CodigoBarrasInput';
import { SelectInput } from '@core/components/Select';
import { Divider, Grid } from '@material-ui/core';
import InputBase from '@material-ui/core/InputBase';
import InputMonetario from 'app/components/Input/InputMonetario';
import SelectConta from 'app/components/Select/SelectConta';
import SelectImposto from 'app/components/Select/SelectImposto';
import { FieldArray } from 'formik';
import NP from 'number-precision';
import React, { useEffect, useState } from 'react';
import { ContasPagarUtil, initRetencoes } from '../store/contasPagarAction';
import FormaDePagamento from './FormaDePagamento';

const handleRetencoes = (handleChange, retencoes, arrayHelpers) => {
  return (event) => {
    if (!retencoes) {
      arrayHelpers.push(initRetencoes);
    }
    handleChange(event);
  };
};

/*
RNE-091 - Cálculo do valor do imposto : Public <<Functional>> Feature
O campo "Valor (R$)" na aba "Retenções", será exibido sempre habilitado para edição, mas o campo será exibido já calculado considerando o valor bruto da despesa e a alíquota do imposto selecionado.

Valor (R$) = Valor total bruto * (alíquota/100)

*/
const handleImposto = (index, handleChange, values, arrayHelpers) => {
  return (event) => {
    handleRetencoes(handleChange, values?.retencoes, arrayHelpers)(event);
    const baseCalculo = ContasPagarUtil.calcularValorTotalBruto(values);
    const imposto = event?.target?.value;
    const valorImposto = ContasPagarUtil.arredondaCasasDecimais(
      NP.times(baseCalculo, NP.divide(imposto?.aliquota || 0, 100)),
      2
    );
    handleChange({ target: { id: `retencoes[${index}].valor`, value: valorImposto } });

    // Deve-se recalcular as parcelas.
    if (!values.retencoes) {
      values.retencoes = [{}];
    }
    values.retencoes[index].valor = valorImposto;
    const valorbase = ContasPagarUtil.calcularValorTotalLiquido(values);
    ContasPagarUtil.handleGerarParcelas(values, handleChange, valorbase)();
  };
};

const Retencao = ({ index, retencao, handleChange, values, arrayHelpers, handleBlur, errors }) => {
  const origemCompraServico = values.origemLancamento === 'COMPRA_SERVICO';
  /*
  RNE-478 - Edição de conta a pagar
  Para as contas a pagar que estiverem com situação "Baixada", somente a aba “Anexar documento” deve ser exibida com possibilidade de edição. Todos os outros campos das outras abas devem ser exibidos desabilitados para edição.
  */
  const contaBaixada = values?.situacao === 'BAIXADA' || values?.situacao === 'PARCIALMENTE_BAIXADA';

  /*
  RNE-478 - Edição de conta a pagar
  Para as contas a pagar que estiverem com a situação “Parcial”, somente as abas “Anexar documento” e “Baixa da conta principal” deve ser exibida com possibilidade de edição. Todos os outros campos das outras abas devem ser exibidos desabilitados para edição. 
  */
  const contaParcial = values?.situacao === 'PARCIAL';

  /*
  RNE-114 - Edição de despesa parcialmente baixada 
  Quando a despesa estiver parcialmente baixada, o sistema deve obedecer as seguintes regras:

  - Para a conta que é uma retenção aberta (que possui conta principal baixada): Não deve permitir edição dos campos “Imposto” e “Valor” 
    e não deve ser permitido informar mais retenção. Nesse caso, como a conta principal está baixada, ela deve ser exibida com todos os 
    campos desabilitados.

  - Para a conta principal aberta (que possui retenção baixada): Não deve permitir edição de "Mês competência", "Valor" e "Categoria". 
    Nesse caso, como a conta da retenção está baixada, ela deve ser exibida com todos os campos desabilitados. Quando a conta estiver sido 
    lançada por código de barras, o campo "Código de barras" também deve ser exibido desabilitado para edição.
  */
  const contaOrigemBaixadaComSituacaoParcialmenteBaixada =
    contaOrigemBaixada() && values?.situacao === 'PARCIALMENTE_BAIXADA';

  function contaOrigemBaixada() {
    const somatorioValorPagoBaixas = values?.baixas?.reduce((acc, baixa) => acc + baixa.valorPago, 0);
    const valorLiquido = values?.valorLiquido;

    return somatorioValorPagoBaixas >= valorLiquido;
  }

  return (
    <>
      <Grid container item spacing={3}>
        <Grid item xs={12}>
          <FormTitle title={`Retenção ${index + 1}`} />
        </Grid>
      </Grid>

      <Grid item xs={12} sm={4}>
        <SelectImposto
          autoFocus={index === 0}
          id={`retencoes[${index}].imposto`}
          name={`retencoes[${index}].imposto`}
          fullWidth
          required
          label="Imposto"
          value={retencao?.imposto}
          onChange={handleImposto(index, handleChange, values, arrayHelpers)}
          onBlur={handleBlur}
          maxLength={100}
          touched={!!errors?.retencoes?.[index]?.imposto}
          error={errors?.retencoes?.[index]?.imposto}
          disabled={
            contaOrigemBaixadaComSituacaoParcialmenteBaixada
              ? true
              : origemCompraServico || contaBaixada || contaParcial
          }
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <DateInput
          fullWidth
          required
          id={`retencoes[${index}].dataVencimento`}
          name={`retencoes[${index}].dataVencimento`}
          label="Data do vencimento"
          value={retencao?.dataVencimento}
          onChange={handleRetencoes(handleChange, values?.retencoes, arrayHelpers)}
          helperText={errors?.retencoes?.[index]?.dataVencimento}
          error={!!errors?.retencoes?.[index]?.dataVencimento}
          disabled={
            contaOrigemBaixadaComSituacaoParcialmenteBaixada
              ? false
              : origemCompraServico || contaBaixada || contaParcial
          }
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputMonetario
          fullWidth
          required
          id={`retencoes[${index}].valor`}
          label="Valor (R$)"
          handleChange={handleRetencoes(handleChange, values?.retencoes, arrayHelpers)}
          value={retencao?.valor}
          handleBlur={handleBlur}
          /*
      RNE-091 - Cálculo do valor do imposto : Public <<Functional>> Feature

      O campo "Valor (R$)" na aba "Retenções", será exibido sempre habilitado para edição, mas o campo será exibido já calculado considerando o valor bruto da despesa e a alíquota do imposto selecionado.

      Valor (R$) = Valor total bruto * (alíquota/100)
      */
          helperText={errors?.retencoes?.[index]?.valor}
          error={!!errors?.retencoes?.[index]?.valor}
          touched={!!errors?.retencoes?.[index]?.valor}
          disabled={
            contaOrigemBaixadaComSituacaoParcialmenteBaixada
              ? true
              : origemCompraServico || contaBaixada || contaParcial
          }
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <SelectInput
          id={`retencoes[${index}].contribuinte`}
          label="Contribuinte"
          onChange={handleRetencoes(handleChange, values?.retencoes, arrayHelpers)}
          value={retencao?.contribuinte}
          options={[
            { id: 'EMPRESA', title: 'Empresa' },
            { id: 'FORNECEDOR', title: 'Fornecedor' },
          ]}
          disabled={contaOrigemBaixadaComSituacaoParcialmenteBaixada ? false : contaBaixada || contaParcial}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <SelectConta
          id={`retencoes[${index}].conta`}
          fullWidth
          label="Conta"
          value={retencao?.conta}
          onChange={handleRetencoes(handleChange, values?.retencoes, arrayHelpers)}
          disabled={contaOrigemBaixadaComSituacaoParcialmenteBaixada ? false : contaBaixada || contaParcial}
          onBlur={handleBlur}
          maxLength={100}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormaDePagamento
          id={`retencoes[${index}].formaPagamento`}
          value={retencao?.formaPagamento}
          handleChange={handleRetencoes(handleChange, values?.retencoes, arrayHelpers)}
          handleBlur={handleBlur}
          maxLength={100}
          tipoConta={retencao?.conta?.tipo}
          disabled={contaOrigemBaixadaComSituacaoParcialmenteBaixada ? false : contaBaixada || contaParcial}
        />
      </Grid>
      <Grid item xs={12} sm={11}>
        <CodigoBarrasInput
          id={`retencoes[${index}].linhaDigitavel`}
          name={`retencoes[${index}].linhaDigitavel`}
          onChange={(event) => {
            handleRetencoes(handleChange, values?.retencoes, arrayHelpers)(event);
          }}
          onBlur={handleBlur}
          value={retencao?.linhaDigitavel}
          fullWidth
          variant="outlined"
          label="Código de barras"
          disabled={contaOrigemBaixadaComSituacaoParcialmenteBaixada ? false : contaBaixada || contaParcial}
        />
      </Grid>

      {index > 0 && !origemCompraServico && !contaBaixada && !contaParcial && (
        <Grid item xs={1}>
          <DeleteItemButton onClick={() => arrayHelpers.remove(index)} tooltip="Excluir retenção" />
        </Grid>
      )}
    </>
  );
};

const ContasPagarRetencao = ({ values, handleChange, handleBlur, errors, touched }) => {
  const retencoes = values?.retencoes || [initRetencoes];
  const valorTotalBruto = ContasPagarUtil.calcularValorTotalBruto(values);
  const valorTotalLiquido = ContasPagarUtil.calcularValorTotalLiquido(values);

  const [valorBruto, setValorBruto] = useState(valorTotalBruto);

  const [valorLiquido, setValorLiquido] = useState(valorTotalLiquido);

  const origemCompraServico = values.origemLancamento === 'COMPRA_SERVICO';
  /*
  RNE-478 - Edição de conta a pagar
  Para as contas a pagar que estiverem com situação "Baixada", somente a aba “Anexar documento” deve ser exibida com possibilidade de edição. Todos os outros campos das outras abas devem ser exibidos desabilitados para edição.
  */
  const contaBaixada = values?.situacao === 'BAIXADA' || values?.situacao === 'PARCIALMENTE_BAIXADA';

  /*
  RNE-478 - Edição de conta a pagar
  Para as contas a pagar que estiverem com a situação “Parcial”, somente as abas “Anexar documento” e “Baixa da conta principal” deve ser exibida com possibilidade de edição. Todos os outros campos das outras abas devem ser exibidos desabilitados para edição. 
  */
  const contaParcial = values?.situacao === 'PARCIAL';

  useEffect(() => {
    setValorBruto(valorTotalBruto);
  }, [valorTotalBruto]);

  useEffect(() => {
    setValorLiquido(valorTotalLiquido);
  }, [valorTotalLiquido]);

  return (
    <>
      <FieldArray name="retencoes">
        {(arrayHelpers) => {
          const retencoesAdicionadas =
            retencoes &&
            retencoes.map((retencao, index) => {
              return (
                <Retencao
                  key={index}
                  index={index}
                  retencao={retencao}
                  handleChange={handleChange}
                  values={values}
                  arrayHelpers={arrayHelpers}
                  handleBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  disabled={contaBaixada || contaParcial}
                />
              );
            });
          return (
            <>
              {retencoesAdicionadas}
              {!origemCompraServico && !contaBaixada && !contaParcial && (
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                  <AddItemButton onClick={() => arrayHelpers.push(initRetencoes)}>
                    Adicionar mais retenção
                  </AddItemButton>
                </Grid>
              )}
            </>
          );
        }}
      </FieldArray>
      <Grid item xs={12}>
        <div>
          <InputBase
            inputProps={{ 'aria-label': 'naked' }}
            fullWidth
            value={`Valor total bruto (R$): ${new Intl.NumberFormat('pt-BR', {
              style: 'decimal',
              minimumFractionDigits: 2,
            }).format(valorBruto)}`}
            disabled={origemCompraServico || contaBaixada || contaParcial}
          />
        </div>
        <div>
          <InputBase
            inputProps={{ 'aria-label': 'naked' }}
            fullWidth
            value={`Valor total líquido (R$): ${new Intl.NumberFormat('pt-BR', {
              style: 'decimal',
              minimumFractionDigits: 2,
            }).format(valorLiquido)}`}
            disabled={origemCompraServico || contaBaixada || contaParcial}
          />
        </div>
      </Grid>

      <Grid xs={12} item spacing={3}>
        <Divider style={{ background: 'rgba(0,0,0,0.5)' }} />
      </Grid>
    </>
  );
};

export default ContasPagarRetencao;
